import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { finalize, firstValueFrom } from 'rxjs';
import { CustomerService } from '../../services/customer/customer.service';
import { CreditPlan } from '../../model/CreditPlan';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Customer } from 'src/app/model/Customer';
import { UserService } from 'src/app/services/user/user.service';
import { CreateTransactionRequestData, TransactionRequestService } from 'src/app/services/transaction-request/transaction-request.service';
import { FileService } from 'src/app/services/file/file.service';

/** Dialog data provided to Submit Request Dialog */
export interface SubmitRequestDialogData {
  customer: Customer;
}

/** Form data interface */
interface SubmitRequestFormData {
  amount: number;
  ponumber: string;
  inumber: string;
  planId: number;
  invoice: File[] | null;
  memo: string;
}

@Component({
    selector: 'app-submit-request-dialog',
    templateUrl: './submit-request-dialog.component.html',
    styleUrl: './submit-request-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SubmitRequestDialogComponent implements OnInit {
  /** Dependency injections */
  private readonly _dialogRef: MatDialogRef<SubmitRequestDialogComponent> = inject(MatDialogRef);
  private readonly _customerService: CustomerService = inject(CustomerService);
  private readonly _transactionRequestService: TransactionRequestService = inject(TransactionRequestService);
  private readonly _toastrService: ToastrService = inject(ToastrService);
  private readonly _userService: UserService = inject(UserService);
  private readonly _fileService: FileService = inject(FileService);

  /** Dialog data */
  public data: SubmitRequestDialogData = inject(MAT_DIALOG_DATA);

  /** Dynamic form variables */
  submitForm: FormGroup;
  creditPlans: CreditPlan[] = [];
  isSubmitting = false;
  uploadedFiles: File[] = [];
  amountToRequest: number = 0;

  /** Loading flags */
  isLoadingCreditPlans: boolean = true;

  /** Private memory */
  private _supplierId!: string;

  /**
   * On construct, initializes the submit form
   */
  constructor() {
    this.submitForm = new FormGroup({
      amount: new FormControl('', [
        Validators.required,
        Validators.min(0.01),
        Validators.max(this.data.customer.availableBalance),
        Validators.pattern(/^\d+(\.\d{1,2})?$/) // Allows numbers with up to 2 decimals
      ]),
      ponumber: new FormControl(''),
      inumber: new FormControl(''),
      planId: new FormControl('', [Validators.required]),
      invoice: new FormControl<File[] | null>(null, [this.invoiceValidator()]),
      memo: new FormControl('')
    });
  }

  /**
   * On initialization, sets up reactive form value changes
   */
  ngOnInit(): void {
    // Reset credit plans
    this.creditPlans = [];
    this.submitForm.get('planId')?.setValue('');

    // Get supplier's company id
    const companyId = "" + this._userService.company?.companyId;
    if (!companyId) {
      throw new Error('Company ID is undefined');
    }
    this._supplierId = companyId;

    // Fetch credit plans based on customer ID and transaction amount
    this._customerService.getCreditPlans$(this._supplierId)
      .pipe(finalize(() => this.isLoadingCreditPlans = false))
      .subscribe({
        next: (plans: CreditPlan[]) => {
          this.creditPlans = plans;
        },
        error: (err: any) => {
          console.error('[SUBMIT-REQUEST-DIALOG-COMPONENT]: Error fetching credit plans:', err);
        }
      });
  }

  /**
   * Handles file input changes
   * 
   * @param event The change event from the file input
   */
  public onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;

    // If files are present, add them
    if (target.files) {
      const newFiles = Array.from(target.files);
      this.uploadedFiles = [...this.uploadedFiles, ...newFiles];
      this.submitForm.get('invoice')?.setValue(this.uploadedFiles);
    }
  }

  /**
   * Remove a file from the list of uploaded files
   * 
   * @param index File index to remove (splice)
   */
  public removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.submitForm.get('invoice')?.setValue(this.uploadedFiles.length > 0 ? this.uploadedFiles : null);
  }

  /**
   * Validator for invoice file types
   * 
   * @returns Angular validator function
   */
  private invoiceValidator(): ValidatorFn {
    const allowedFormats = ['application/pdf', 'image/jpeg', 'image/png'];
    const maxSize = 5 * 1024 * 1024; // 5 MB

    return (control: AbstractControl<File[] | null>): ValidationErrors | null => {
      // If no value, no errors
      if (!control.value || control.value.length === 0) {
        return null;
      }

      // Get files
      const files = control.value;
      const errors: ValidationErrors = {};

      for (const file of files) {
        // Is file format allowed
        if (!allowedFormats.includes(file.type)) {
          errors['format'] = true;
          break;
        }

        // Is file size allowed
        if (file.size > maxSize) {
          errors['size'] = true;
          break;
        }
      }

      // Return any present errors
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  /**
   * Handle submit form submission
   */
  public async submit(): Promise<void> {
    // Check if the form is valid
    if (this.submitForm.invalid) {
      this._toastrService.error('Please fill in all required fields correctly');
      return;
    }

    this.isSubmitting = true;

    try {
      // Upload files
      const fileIds = await this.uploadFiles(this.uploadedFiles);

      // Create request data to send
      const formValues: SubmitRequestFormData = this.submitForm.value;
      const request: CreateTransactionRequestData = {
        amount: Number(formValues.amount),
        buyerId: this.data.customer.id,
        memo: formValues.memo || '',
        planId: Number(formValues.planId) || null,
        poNumber: formValues.ponumber || '',
        invoiceNumber: formValues.inumber || '',
        fileIds: fileIds,
      };

      // Submit request
      this._transactionRequestService.createRequest$(request)
        .pipe(finalize(() => setTimeout(() => this.isSubmitting = false, 1000)))
        .subscribe({
          next: () => {
            this._toastrService.success('Request submitted successfully');
            this.close(true);
          },
          error: (err) => {
            console.log('[SUBMIT-REQUEST-DIALOG-COMPONENT]: could not submit request', err);
            this._toastrService.error('Failed to submit request');
          }
        });
    } catch (err) {
      // Catch errors from form validation or transaction service
      console.error('[SUBMIT-REQUEST-DIALOG-COMPONENT]: Error submitting request:', err);
      this._toastrService.error('Failed to submit request');
      this.isSubmitting = false;
    }
  }

  /**
   * Helper function to upload files
   * 
   * @param files Files to upload
   * @return Array of uploaded file IDs
   */
  public async uploadFiles(files: File[]): Promise<string[]> {
    // Upload files if provided
    if (files.length == 0) {
      return [];
    }

    // Upload all files in parallel
    try {
      const uploadPromises = files.map(file =>
        firstValueFrom(this._fileService.uploadInvoice(file))
      );

      const fileIds = await Promise.all(uploadPromises);
      return fileIds.filter(id => id !== null) as string[];
    } catch (err) {
      console.error('[SUBMIT-REQUEST-DIALOG-COMPONENT]: Error uploading files:', err);
      throw new Error('File upload failed');
    }
  }

  /**
   * Close the dialog
   */
  public close(success: boolean = false) {
    this._dialogRef.close({ success });
  }
}
